import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { GlobalSearchGroup, GlobalSearchItem, searchGlobal } from '../services/globalSearchService';
import { isAbortError } from '../utils/listSearch';

type GlobalSearchProps = {
  /** Compact trigger for mobile (icon opens overlay/panel). */
  mobileTrigger?: boolean;
  className?: string;
  autoFocus?: boolean;
  onClose?: () => void;
};

const GlobalSearch: React.FC<GlobalSearchProps> = ({
  mobileTrigger = false,
  className = '',
  autoFocus = false,
  onClose,
}) => {
  const navigate = useNavigate();
  const listId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<GlobalSearchGroup[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [mobileOpen, setMobileOpen] = useState(false);

  const flatItems = groups.flatMap((g) => g.items);
  const trimmed = query.trim();
  const showHint = open && trimmed.length > 0 && trimmed.length < 3;
  const showDropdown = open && (loading || showHint || trimmed.length >= 3);

  const closePanel = useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
    onClose?.();
  }, [onClose]);

  const runSearch = useCallback(async (term: string) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    if (term.trim().length < 3) {
      setGroups([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const result = await searchGlobal(term, controller.signal);
      if (!controller.signal.aborted) {
        setGroups(result);
        setActiveIndex(result.length > 0 ? 0 : -1);
      }
    } catch (error) {
      if (isAbortError(error)) return;
      if (!controller.signal.aborted) {
        setGroups([]);
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (open || mobileOpen) {
        runSearch(query);
      }
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, open, mobileOpen, runSearch]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (autoFocus || mobileOpen) {
      inputRef.current?.focus();
    }
  }, [autoFocus, mobileOpen]);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
        if (mobileOpen) {
          setMobileOpen(false);
          onClose?.();
        }
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [mobileOpen, onClose]);

  const selectItem = (item: GlobalSearchItem) => {
    navigate(item.path);
    setQuery('');
    setGroups([]);
    setOpen(false);
    setMobileOpen(false);
    onClose?.();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closePanel();
      setMobileOpen(false);
      return;
    }

    if (!showDropdown || flatItems.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((prev) => (prev + 1) % flatItems.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prev) => (prev <= 0 ? flatItems.length - 1 : prev - 1));
    } else if (event.key === 'Enter' && activeIndex >= 0 && flatItems[activeIndex]) {
      event.preventDefault();
      selectItem(flatItems[activeIndex]);
    }
  };

  const renderDropdown = () => {
    if (!showDropdown) return null;

    return (
      <div
        id={listId}
        role="listbox"
        className="absolute left-0 right-0 top-full z-50 mt-1 max-h-96 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:!bg-gray-800"
      >
        {showHint && (
          <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
            Escribí al menos 3 letras
          </div>
        )}

        {trimmed.length >= 3 && loading && (
          <div className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">Buscando…</div>
        )}

        {trimmed.length >= 3 && !loading && groups.length === 0 && (
          <div className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
            Sin resultados para “{trimmed}”
          </div>
        )}

        {trimmed.length >= 3 &&
          !loading &&
          groups.map((group) => (
            <div
              key={group.type}
              className="border-b border-gray-100 last:border-b-0 dark:border-gray-700"
            >
              <div className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {group.label}
              </div>
              <ul>
                {group.items.map((item) => {
                  const flatIdx = flatItems.findIndex(
                    (f) => f.type === item.type && f.id === item.id,
                  );
                  const active = flatIdx === activeIndex;
                  return (
                    <li key={`${item.type}-${item.id}`}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={active}
                        className={`flex w-full flex-col items-start px-3 py-2 text-left text-sm transition-colors ${
                          active
                            ? 'bg-primary-50 text-primary-800 dark:bg-primary-900/30 dark:text-primary-200'
                            : 'text-gray-900 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700'
                        }`}
                        onMouseEnter={() => setActiveIndex(flatIdx)}
                        onClick={() => selectItem(item)}
                      >
                        <span className="font-medium">{item.title}</span>
                        {item.subtitle ? (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {item.subtitle}
                          </span>
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
      </div>
    );
  };

  const searchInput = (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
      <input
        ref={inputRef}
        type="search"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="Buscar clientes, productos, remitos…"
        className="search-input w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 transition-all duration-200 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-400"
        autoComplete="off"
        aria-autocomplete="list"
        aria-controls={listId}
        aria-expanded={showDropdown}
      />
      {renderDropdown()}
    </div>
  );

  if (mobileTrigger) {
    return (
      <>
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="p-2 text-gray-600 hover:bg-gray-100 hover:text-primary-500 rounded-lg transition-all duration-200 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-primary-400 md:hidden"
          aria-label="Buscar"
        >
          <MagnifyingGlassIcon className="h-6 w-6" />
        </button>
        {mobileOpen && (
          <div className="fixed inset-0 z-[60] bg-black/40 md:hidden">
            <div className="bg-white p-3 shadow-lg dark:!bg-gray-900">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Buscar</span>
                <button
                  type="button"
                  onClick={() => {
                    setMobileOpen(false);
                    setOpen(false);
                    onClose?.();
                  }}
                  className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
                  aria-label="Cerrar búsqueda"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              {searchInput}
            </div>
          </div>
        )}
      </>
    );
  }

  return searchInput;
};

export default GlobalSearch;
